import { useQuery } from "@tanstack/react-query";
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  Badge,
  Banner,
  Spinner,
  SkeletonBodyText,
  Button,
  InlineStack,
} from "@shopify/polaris";
import {
  getOrBootstrapShop,
  pricingPlansUrl,
  INSTALL_STATUS,
  type CreditState,
} from "../services/brilio";
import { redirectTopFrame } from "../config/shopify-app-bridge";
import { OnboardingChecklist } from "../components/OnboardingChecklist";
import { ReviewAsk, hasCreditWarning } from "../components/ReviewAsk";

/**
 * Shop status view — install status, plan, and message usage. All fields come
 * straight from the live `GET /shopify/admin/shops/me` (no analytics: the
 * backend doesn't track conversation/attribution metrics, so we don't invent
 * them here).
 */
/**
 * The warning a merchant never got.
 *
 * Credits — not `messages_used` — are what stop the storefront answering, and
 * until now nothing surfaced them. The first sign of trouble was shoppers being
 * told the assistant is unavailable, with the merchant none the wiser.
 *
 * Silent while there is nothing to act on: a banner that is always present is
 * one a merchant stops reading, and this one has to be believed on the day it
 * matters. `undefined` (an older backend that does not send the field yet)
 * counts as nothing to say.
 */
function CreditWarning({
  state,
  plansUrl,
}: {
  state?: CreditState;
  plansUrl: string | null;
}) {
  // The predicate is imported, not repeated here: the review ask below has to
  // hide exactly when this banner shows, and a fifth CreditState added to one
  // of them without the other is how this app would end up asking for a review
  // over "your assistant has stopped answering". A type predicate, so `state`
  // still narrows to "low" | "critical" | "empty" for the reads below.
  if (!hasCreditWarning(state)) return null;

  // The banner used to tell the merchant to upgrade and give them nothing to
  // press. Telling someone their store is losing sales and leaving them to hunt
  // for the fix is worse than saying nothing — and an embedded app instructing
  // an action it does not provide is a Shopify review finding in its own right.
  // Labelled "Choose a plan", not "View plans": the Plan row carries a
  // permanent "View plans" link, and two buttons with the same accessible name
  // on one page is ambiguous to a screen reader and to anyone scanning it. The
  // urgent, primary control also reads better as the action it wants.
  const action = plansUrl ? (
    <Button onClick={() => redirectTopFrame(plansUrl)}>Choose a plan</Button>
  ) : null;

  if (state === "empty") {
    return (
      <Banner tone="critical" title="Your assistant has stopped answering">
        <BlockStack gap="200">
          <p>
            You&rsquo;ve used all your credits, so shoppers are being told the
            assistant is unavailable. Choose a plan to start answering again.
          </p>
          {action}
        </BlockStack>
      </Banner>
    );
  }

  // "low" and "critical" differ in how much time is left, so they differ in
  // tone — but both say the same actionable thing.
  const critical = state === "critical";
  return (
    <Banner
      tone={critical ? "warning" : "info"}
      title={
        critical
          ? "Your assistant is about to stop answering"
          : "Your credits are running low"
      }
    >
      <BlockStack gap="200">
        <p>
          {critical
            ? "You have very few credits left. When they run out, shoppers will be told the assistant is unavailable."
            : "You still have credits, but at your current rate the assistant may stop answering before the period ends."}
        </p>
        {action}
      </BlockStack>
    </Banner>
  );
}

export default function Dashboard() {
  const { data: shop, isLoading } = useQuery({
    queryKey: ["shop", "me"],
    queryFn: getOrBootstrapShop,
    // While the install is still provisioning, poll so the view flips to live.
    refetchInterval: (q) =>
      q.state.data?.install_status === INSTALL_STATUS.installing
        ? 3_000
        : false,
  });

  if (isLoading) {
    return (
      <Page title="Assistant">
        <SkeletonBodyText lines={8} />
      </Page>
    );
  }

  if (!shop) {
    return (
      <Page title="Assistant">
        <Banner tone="critical">
          Unable to load your shop. Try reloading.
        </Banner>
      </Page>
    );
  }

  if (shop.install_status === INSTALL_STATUS.installing) {
    return (
      <Page title="Setting up your assistant…">
        <Card>
          <BlockStack gap="400">
            <Text as="p">
              We&rsquo;re connecting your store. This page updates
              automatically.
            </Text>
            <Spinner
              accessibilityLabel="Setting up your assistant"
              size="small"
            />
          </BlockStack>
        </Card>
      </Page>
    );
  }

  // Only "active" is live. Anything else — failed, uninstalled, redacted, or an
  // unknown status — is NOT live and must not fall through to the live view.
  if (shop.install_status !== INSTALL_STATUS.active) {
    const failed = shop.install_status === INSTALL_STATUS.failed;
    return (
      <Page title={failed ? "Setup didn't finish" : "Assistant not active"}>
        <Banner tone={failed ? "critical" : "warning"}>
          {failed
            ? "The assistant setup ran into an issue. Try reinstalling the app, or email support@brilio.ai."
            : `Your assistant isn't active (status: ${shop.install_status}). If you just installed, give it a moment; otherwise try reinstalling the app or email support@brilio.ai.`}
        </Banner>
      </Page>
    );
  }

  // Resolved once and shared by the low-credit banner and the Plan row. Null
  // when the shop domain or app handle is missing, in which case both callers
  // render nothing rather than a link to nowhere.
  const plansUrl = pricingPlansUrl(shop.shop_domain, shop.app_handle);

  return (
    <Page
      title="Assistant"
      subtitle={
        shop.concierge.is_active
          ? `Your assistant is set up for ${shop.shop_domain}.`
          : `Your assistant is paused — shoppers on ${shop.shop_domain} won't see it.`
      }
    >
      <BlockStack gap="400">
        <CreditWarning state={shop.credit_state} plansUrl={plansUrl} />
        {/* Above the status cards on purpose: on a fresh install all three of
            its steps are outstanding, and what the merchant needs first is the
            list of what is left — not a plan row. It collapses to a one-line
            confirmation once they are all done, so an established shop is not
            paying for it. */}
        <OnboardingChecklist shop={shop} />
        <Layout>
          <Layout.Section>
            <Card>
              <BlockStack gap="300">
                <Text as="h3" variant="headingSm">
                  Status
                </Text>
                <BlockStack gap="200">
                  <Row label="Assistant">
                    {/* Deliberately labelled "Assistant", not "Storefront". This
                        badge is computed from install_status + is_active, which
                        say whether the assistant is RUNNING — they cannot say
                        whether the merchant has added the app embed to their
                        theme, and until they have, no shopper sees anything.
                        Labelling this "Storefront: Live" told merchants their
                        chat was on their store when it was not; the setup
                        checklist above is where that question is answered. */}
                    {/* Wrapped: `Row` lays its children out in a BlockStack,
                        which is a column flex container, so a bare Badge
                        stretches to the full card width and reads as a status
                        BAR rather than a pill. InlineStack sizes it to its
                        content. Caught by screenshotting the real page for the
                        listing assets. */}
                    <InlineStack>
                      {shop.concierge.is_active ? (
                        <Badge tone="success">Live</Badge>
                      ) : (
                        <Badge tone="attention">Paused</Badge>
                      )}
                    </InlineStack>
                  </Row>
                  <Row label="Plan">
                    <InlineStack gap="200" blockAlign="center">
                      <Text as="span">{shop.plan}</Text>
                      {/*
                        Shown whatever the credit state, not only when credits
                        run low. The App Store reviewer notes tell the reviewer
                        that "the Dashboard's View plans button opens Shopify's
                        own plan picker" — and on a fresh install _credit_state
                        is "ok", so the low-credit banner (the only other place
                        this button lived) never renders at all.

                        Still conditional on the URL resolving: pricingPlansUrl
                        returns null when the shop domain or app handle is
                        missing, and its contract is that the caller hides the
                        control rather than offering a link to nowhere.
                      */}
                      {plansUrl ? (
                        <Button
                          variant="plain"
                          onClick={() => redirectTopFrame(plansUrl)}
                        >
                          View plans
                        </Button>
                      ) : null}
                    </InlineStack>
                  </Row>
                  {shop.charge_status ? (
                    <Row label="Billing">
                      <Text as="span" tone="subdued">
                        {shop.charge_status}
                      </Text>
                    </Row>
                  ) : null}
                </BlockStack>
              </BlockStack>
            </Card>
          </Layout.Section>

          <Layout.Section variant="oneThird">
            <Card>
              <BlockStack gap="200">
                <Text as="h3" variant="headingSm">
                  Messages answered
                </Text>
                <Text as="p" tone="subdued">
                  {shop.messages_used.toLocaleString()} since install
                </Text>
                {/*
                  Deliberately a count, not a meter.

                  `messages_quota` is written once at install as the trial's
                  1,000 and never updated when a merchant subscribes, and
                  `messages_used` is never reset at period rollover. So the bar
                  was measuring a running total against a number that stopped
                  being true the moment anyone paid: a Growth merchant would
                  read "3,500 / 1,000 messages" against a red bar, next to a
                  "Resets" date fixed at install + 14 days and long past.

                  Credits are what actually stop the storefront answering, and
                  CreditWarning above already surfaces those. A second meter
                  measuring something that enforces nothing is worse than no
                  meter — it invites a merchant to plan around a limit that is
                  not the limit.
                */}
              </BlockStack>
            </Card>
          </Layout.Section>
        </Layout>
        {/* Last on the page, deliberately. An ask is not news: it sits below
            the merchant's own status, never above the credit banner or the
            setup checklist, and it renders nothing at all until the assistant
            has been answering shoppers for a while on a finished setup. */}
        <ReviewAsk shop={shop} />
      </BlockStack>
    </Page>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <BlockStack gap="050">
      <Text as="p" tone="subdued" variant="bodySm">
        {label}
      </Text>
      {children}
    </BlockStack>
  );
}
